// src/pages/RapportClient.js - Rapport global d'activité d'un client
// (Gestionnaire) : toutes les actions site par site sur une période donnée -
// agents affectés, main courante, tâches, incidents, rondes.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
};

const formatDateTime = (v) => {
  if (!v) return '-';
  try { return new Date(v.replace(' ', 'T')).toLocaleString('fr-FR'); } catch { return v; }
};

const NIVEAU_LABELS = { 1: 'Majeur', 2: 'Elevé', 3: 'Normal', 4: 'Basse' };

const RapportClient = () => {
  const [clients, setClients] = useState([]);
  const [idClient, setIdClient] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [error, setError] = useState("");
  const [societeLogo, setSocieteLogo] = useState(null);
  const [nomSociete, setNomSociete] = useState("");
  const [societeInfoLigne, setSocieteInfoLigne] = useState("");

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("access_token")}` });

  useEffect(() => {
    axios.get(`${API_BASE_URL}/clients/list.php?limit=2000`, { headers: authHeaders() })
      .then(res => setClients(res.data?.data?.clients || res.data?.clients || []))
      .catch(() => setError("Impossible de charger la liste des clients."))
      .finally(() => setLoadingClients(false));

    // Logo de la société : embarqué dans le PDF exporté ci-dessous.
    axios.get(`${API_BASE_URL}/societes/my_logo.php`, { headers: authHeaders() })
      .then(res => {
        if (res.data?.logo_base64) setSocieteLogo(res.data.logo_base64);
        if (res.data?.nom_societe) setNomSociete(res.data.nom_societe);
        const ligne = [res.data?.address_societe, res.data?.tel_societe].filter(Boolean).join(" — ");
        if (ligne) setSocieteInfoLigne(ligne);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const genererRapport = useCallback(async () => {
    if (!idClient) {
      setError("Sélectionnez un client.");
      return;
    }
    setLoading(true);
    setError("");
    setRapport(null);
    try {
      const params = new URLSearchParams({ id_client: idClient });
      if (dateDebut) params.set('date_debut', dateDebut);
      if (dateFin) params.set('date_fin', dateFin);

      const res = await axios.get(`${API_BASE_URL}/rapports/client_activite.php?${params.toString()}`, {
        headers: authHeaders()
      });
      if (res.data?.success) {
        setRapport(res.data);
      } else {
        setError(res.data?.message || "Erreur lors de la génération du rapport.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la génération du rapport.");
    } finally {
      setLoading(false);
    }
  }, [idClient, dateDebut, dateFin]);

  const exportPDF = () => {
    if (!rapport) return;
    const doc = new jsPDF('p', 'mm', 'a4');

    // En-tête : logo à gauche, nom/adresse/tél. de la société alignés sur la
    // même ligne (à droite du logo, pas centrés en dessous), puis le titre du
    // rapport sous l'ensemble, puis un séparateur avant le reste du contenu.
    const logoSize = 24;
    const logoTop = 8;
    let logoBottom = logoTop;
    if (societeLogo) {
      const format = societeLogo.startsWith('data:image/png') ? 'PNG'
        : societeLogo.startsWith('data:image/webp') ? 'WEBP'
        : 'JPEG';
      try {
        doc.addImage(societeLogo, format, 14, logoTop, logoSize, logoSize);
        logoBottom = logoTop + logoSize;
      } catch (e) {
        // Logo illisible (ex: SVG non supporté par jsPDF) : on continue sans bloquer l'export.
      }
    }

    const infoX = 14 + logoSize + 6;
    let infoY = logoTop + 6;
    if (nomSociete) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(nomSociete, infoX, infoY);
      infoY += 5;
    }
    if (societeInfoLigne) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      doc.text(societeInfoLigne, infoX, infoY);
      doc.setTextColor(0);
      infoY += 5;
    }

    let sousTitreY = Math.max(logoBottom, infoY) + 6;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`Rapport d'activité — ${rapport.client.nom}`, 105, sousTitreY, { align: 'center' });
    sousTitreY += 5;

    // Séparateur entre le bloc en-tête (logo + société + titre) et le reste.
    const sepY = sousTitreY + 3;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(14, sepY, 196, sepY);
    sousTitreY = sepY + 6;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const periodeLabel = rapport.periode.date_debut || rapport.periode.date_fin
      ? `Période : ${rapport.periode.date_debut || '...'} → ${rapport.periode.date_fin || '...'}`
      : 'Période : toutes dates';
    doc.text(`${periodeLabel} — Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, sousTitreY + 1);

    let y = sousTitreY + 7;
    const pageHeight = doc.internal.pageSize.getHeight();

    const section = (titre, colonnes, lignes) => {
      if (lignes.length === 0) return;
      if (y > pageHeight - 30) { doc.addPage(); y = 15; }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(titre, 14, y);
      y += 3;
      autoTable(doc, {
        startY: y,
        head: [colonnes.map(c => c.label)],
        body: lignes.map(row => colonnes.map(c => String(c.value(row) ?? '-'))),
        theme: 'striped',
        headStyles: { fillColor: [11, 60, 93], fontSize: 8 },
        styles: { fontSize: 7.5 },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 8;
    };

    rapport.sites.forEach((s) => {
      if (y > pageHeight - 30) { doc.addPage(); y = 15; }
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(11, 60, 93);
      doc.text(`Site : ${s.site.nom_site}`, 14, y);
      doc.setTextColor(0, 0, 0);
      y += 7;

      section("Agents affectés", [
        { label: 'Agent', value: r => `${r.prenom_agent} ${r.nom_agent}` },
        { label: 'Matricule', value: r => r.matricule_agent },
        { label: 'Période', value: r => r.periode === 'jour' ? 'Journée' : r.periode === 'soir' ? 'Soirée' : '-' },
      ], s.agents_affectes);

      section("Main courante", [
        { label: 'Date', value: r => formatDateTime(r.created_at) },
        { label: 'Agent', value: r => `${r.prenom_agent} ${r.nom_agent}` },
        { label: 'Motif', value: r => r.motif_main_courante },
      ], s.main_courante);

      section("Incidents", [
        { label: 'Date', value: r => formatDateTime(r.date_creer_incident) },
        { label: 'Ticket', value: r => r.numero_ticket_incident },
        { label: 'Type', value: r => r.type_incident },
        { label: 'Description', value: r => r.description_incident },
        { label: 'Résolu', value: r => r.date_resolus_incident ? formatDateTime(r.date_resolus_incident) : 'Non' },
      ], s.incidents);

      section("Tâches", [
        { label: 'Titre', value: r => r.titre_tache },
        { label: 'Agent', value: r => `${r.prenom_agent} ${r.nom_agent}` },
        { label: 'Statut', value: r => r.statut_tache },
        { label: 'Début', value: r => formatDateTime(r.date_debut) },
      ], s.taches);

      section("Rondes", [
        { label: 'Date', value: r => formatDateTime(r.date_scan) },
        { label: 'Contrôleur/Agent', value: r => `${r.prenom_user} ${r.nom_user}` },
        { label: 'Titre', value: r => r.titre || '-' },
        { label: 'État', value: r => r.incident ? `Incident (${NIVEAU_LABELS[r.incident.niveau] || '?'})` : 'RAS' },
        {
          label: 'Détails incident', value: r => {
            if (!r.incident) return '-';
            const lignes = [
              `#${r.incident.numero_ticket} — ${r.incident.type || 'Type non précisé'}`,
              r.incident.description || '',
              `Action : ${r.incident.action || 'Aucune action affectée'}`,
              r.incident.resolu
                ? `Résolu par ${r.incident.resolveur || 'N/A'} le ${formatDateTime(r.incident.date_resolution)}`
                : 'Non résolu',
            ];
            return lignes.filter(Boolean).join('\n');
          }
        },
      ], s.rondes);

      y += 4;
    });

    doc.save(`rapport_client_${rapport.client.id_client}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportExcel = () => {
    if (!rapport) return;
    const wb = XLSX.utils.book_new();

    const feuille = (nom, lignes, colonnes) => {
      const rows = [];
      rapport.sites.forEach(s => {
        (lignes(s) || []).forEach(r => {
          const obj = { 'Site': s.site.nom_site };
          colonnes.forEach(c => { obj[c.label] = c.value(r); });
          rows.push(obj);
        });
      });
      if (rows.length === 0) rows.push({ 'Site': '(aucune donnée)' });
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, nom);
    };

    feuille("Agents affectés", s => s.agents_affectes, [
      { label: 'Agent', value: r => `${r.prenom_agent} ${r.nom_agent}` },
      { label: 'Matricule', value: r => r.matricule_agent },
      { label: 'Période', value: r => r.periode === 'jour' ? 'Journée' : r.periode === 'soir' ? 'Soirée' : '' },
    ]);
    feuille("Main courante", s => s.main_courante, [
      { label: 'Date', value: r => formatDateTime(r.created_at) },
      { label: 'Agent', value: r => `${r.prenom_agent} ${r.nom_agent}` },
      { label: 'Motif', value: r => r.motif_main_courante },
      { label: 'Description', value: r => r.description_main_courante },
    ]);
    feuille("Incidents", s => s.incidents, [
      { label: 'Date', value: r => formatDateTime(r.date_creer_incident) },
      { label: 'Ticket', value: r => r.numero_ticket_incident },
      { label: 'Type', value: r => r.type_incident },
      { label: 'Description', value: r => r.description_incident },
      { label: 'Résolu le', value: r => r.date_resolus_incident ? formatDateTime(r.date_resolus_incident) : '' },
    ]);
    feuille("Tâches", s => s.taches, [
      { label: 'Titre', value: r => r.titre_tache },
      { label: 'Agent', value: r => `${r.prenom_agent} ${r.nom_agent}` },
      { label: 'Statut', value: r => r.statut_tache },
      { label: 'Début', value: r => formatDateTime(r.date_debut) },
      { label: 'Fin', value: r => formatDateTime(r.date_fin) },
    ]);
    feuille("Rondes", s => s.rondes, [
      { label: 'Date', value: r => formatDateTime(r.date_scan) },
      { label: 'Effectuée par', value: r => `${r.prenom_user} ${r.nom_user}` },
      { label: 'Titre', value: r => r.titre || '' },
      { label: 'État', value: r => r.incident ? `Incident (${NIVEAU_LABELS[r.incident.niveau] || '?'})` : 'RAS' },
      { label: 'Type incident', value: r => r.incident?.type || '' },
      { label: 'Description incident', value: r => r.incident?.description || '' },
      { label: 'Action', value: r => r.incident?.action || '' },
      {
        label: 'Résolution', value: r => r.incident
          ? (r.incident.resolu ? `Résolu par ${r.incident.resolveur || 'N/A'} le ${formatDateTime(r.incident.date_resolution)}` : 'Non résolu')
          : ''
      },
    ]);

    XLSX.writeFile(wb, `rapport_client_${rapport.client.id_client}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '700', fontSize: '1.8rem' }}>
          <i className="bi bi-file-earmark-text me-2"></i>
          Rapport global — Activité client
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Tableau de bord</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Rapport client</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>Client</label>
                <select className="form-select" value={idClient} onChange={(e) => setIdClient(e.target.value)}
                  disabled={loadingClients} style={{ borderColor: '#e0e0e0' }}>
                  <option value="">Sélectionner un client</option>
                  {clients.map(c => (
                    <option key={c.id_client} value={c.id_client}>
                      {c.prenom_client} {c.nom_client}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>Du (optionnel)</label>
                <input type="date" className="form-control" value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)} style={{ borderColor: '#e0e0e0' }} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>Au (optionnel)</label>
                <input type="date" className="form-control" value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)} style={{ borderColor: '#e0e0e0' }} />
              </div>
              <div className="col-md-2">
                <button className="btn w-100 fw-semibold" onClick={genererRapport} disabled={loading}
                  style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary, color: 'white', borderRadius: '8px' }}>
                  {loading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-search me-1"></i>Générer</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {rapport && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 style={{ color: COLORS.primary, fontWeight: '700' }}>{rapport.client.nom}</h5>
                <div className="d-flex gap-3 flex-wrap" style={{ fontSize: '0.85rem', color: '#757575' }}>
                  <span><i className="bi bi-person-badge me-1"></i>{rapport.totaux.agents_affectes} agent(s) affecté(s)</span>
                  <span><i className="bi bi-journal-text me-1"></i>{rapport.totaux.main_courante} main(s) courante(s)</span>
                  <span><i className="bi bi-list-task me-1"></i>{rapport.totaux.taches} tâche(s)</span>
                  <span><i className="bi bi-exclamation-triangle me-1"></i>{rapport.totaux.incidents} incident(s)</span>
                  <span><i className="bi bi-shield-check me-1"></i>{rapport.totaux.rondes} ronde(s)</span>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button className="btn" onClick={exportPDF}
                  style={{ color: COLORS.danger, borderColor: COLORS.danger, borderRadius: '8px' }}>
                  <i className="bi bi-file-earmark-pdf me-1"></i>PDF
                </button>
                <button className="btn" onClick={exportExcel}
                  style={{ color: COLORS.success, borderColor: COLORS.success, borderRadius: '8px' }}>
                  <i className="bi bi-file-earmark-excel me-1"></i>Excel
                </button>
              </div>
            </div>

            {rapport.sites.length === 0 ? (
              <div className="alert alert-info">Ce client n'a aucun site enregistré.</div>
            ) : rapport.sites.map((s) => (
              <div className="card border-0 shadow-sm mb-4" key={s.site.id_site} style={{ borderRadius: '12px' }}>
                <div className="card-header bg-white" style={{ borderBottom: `2px solid ${COLORS.lightBg}` }}>
                  <h6 className="mb-0" style={{ color: COLORS.primary, fontWeight: '700' }}>
                    <i className="bi bi-building me-2"></i>{s.site.nom_site}
                    <span className="text-muted small ms-2">{s.site.addresse_site}</span>
                  </h6>
                </div>
                <div className="card-body">
                  {[
                    ['Agents affectés', 'bi-person-badge', s.agents_affectes, (r) => `${r.prenom_agent} ${r.nom_agent} (${r.matricule_agent || '-'})${r.periode ? ' — ' + (r.periode === 'jour' ? 'Journée' : 'Soirée') : ''}`],
                    ['Main courante', 'bi-journal-text', s.main_courante, (r) => `${formatDateTime(r.created_at)} — ${r.motif_main_courante}`],
                    ['Tâches', 'bi-list-task', s.taches, (r) => `${r.titre_tache} — ${r.statut_tache} (${r.prenom_agent} ${r.nom_agent})`],
                    ['Incidents', 'bi-exclamation-triangle', s.incidents, (r) => `${formatDateTime(r.date_creer_incident)} — ${r.description_incident}`],
                    ['Rondes', 'bi-shield-check', s.rondes, (r) => `${formatDateTime(r.date_scan)} — ${r.prenom_user} ${r.nom_user}`],
                  ].map(([titre, icon, liste, formatter]) => (
                    <div className="mb-3" key={titre}>
                      <div className="fw-semibold mb-1" style={{ color: COLORS.secondary, fontSize: '0.85rem' }}>
                        <i className={`bi ${icon} me-1`}></i>{titre} ({liste.length})
                      </div>
                      {liste.length === 0 ? (
                        <div className="text-muted small">Aucune donnée sur la période.</div>
                      ) : (
                        <ul className="mb-0" style={{ fontSize: '0.85rem' }}>
                          {liste.slice(0, 5).map((r, i) => <li key={i}>{formatter(r)}</li>)}
                          {liste.length > 5 && <li className="text-muted">... et {liste.length - 5} autre(s) (voir export)</li>}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </section>
    </Layout>
  );
};

export default RapportClient;
